/**
 * Client for the project "Ask" SSE endpoint (FXC-9009).
 *
 * POST /api/projects/{id}/ask streams a local-LLM answer to a plain-language
 * project question, grounded in server-computed stats (run table + Tailor
 * campaigns). Same transport discipline as narration.ts: fetch + ReadableStream
 * (EventSource can't set an Authorization header), `{model}` first, `{delta}`
 * token events, terminal `[DONE]`, and a 503 = offline pill, never an error.
 *
 * DEV FALLBACK (pre-cut-over): the running demo API may predate the /ask
 * route (it cannot be restarted mid-campaign). When the route 404s AND we're
 * on a dev build, we assemble a compact context CLIENT-side from endpoints
 * that are already live (run table + campaign detail) and stream straight
 * from the local Ollama (its default CORS allows localhost origins). The
 * `via` tag on onModel makes the path visible in the UI — never silent.
 */

import { getAuthToken } from "./client";
import { fetchRunTablePaginated } from "./runTable";
import { fetchHighliftCampaign, listHighliftCampaigns } from "./highliftCampaign";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const OLLAMA_URL = "http://127.0.0.1:11434";
const OLLAMA_FALLBACK_MODEL = "qwen2.5:14b";

export type AskVia = "api" | "ollama-direct";

/** Agent backends the Ask drawer can select per request (FXC-10027). */
export type AskProvider = "ollama" | "anthropic" | "openai";

export interface AskStreamHandlers {
  onModel?: (model: string, via: AskVia) => void;
  onDelta: (delta: string) => void;
  onOffline?: (reason: string) => void;
  onError?: (reason: string) => void;
  onDone?: () => void;
}

// ---------------------------------------------------------------------------
// The primary path: /api/projects/{id}/ask
// ---------------------------------------------------------------------------

export async function streamAsk(
  projectId: string,
  branch: string,
  question: string,
  handlers: AskStreamHandlers,
  signal?: AbortSignal,
  provider?: AskProvider,
): Promise<void> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/projects/${projectId}/ask`, {
      method: "POST",
      headers,
      body: JSON.stringify({ question, branch, ...(provider ? { provider } : {}) }),
      signal,
    });
  } catch {
    if (signal?.aborted) return;
    handlers.onError?.("network error");
    return;
  }

  if (res.status === 503) {
    let reason = "ask offline";
    try {
      const j = await res.json();
      if (j?.reason) reason = String(j.reason);
    } catch { /* keep default */ }
    handlers.onOffline?.(reason);
    return;
  }
  // The staged route isn't on the RUNNING api yet (pre-cut-over 404/405):
  // dev builds fall back to direct Ollama; everything else reads as offline.
  // A hosted-provider request NEVER falls back — API keys don't belong in the
  // browser, and answering as local qwen while labelled Claude/GPT would lie.
  if (res.status === 404 || res.status === 405) {
    if (import.meta.env.DEV && (!provider || provider === "ollama")) {
      await streamAskDirectOllama(projectId, branch, question, handlers, signal);
      return;
    }
    handlers.onOffline?.(
      provider && provider !== "ollama"
        ? `Ask backend not deployed on this API yet — ${provider} needs the API path`
        : "Ask backend not deployed on this API yet",
    );
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError?.(`ask failed (${res.status})`);
    return;
  }

  await readSseStream(res.body, handlers, signal, "api");
}

/** Parse the SSE frame stream (same wire shape as /highlift/narrate). */
async function readSseStream(
  body: ReadableStream<Uint8Array>,
  handlers: AskStreamHandlers,
  signal: AbortSignal | undefined,
  via: AskVia,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flush = (chunk: string): boolean => {
    buffer += chunk;
    let sep: number;
    // eslint-disable-next-line no-cond-assign
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const data = frame
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      if (data === "[DONE]") {
        handlers.onDone?.();
        return true;
      }
      try {
        const obj = JSON.parse(data) as { model?: string; delta?: string; error?: string };
        if (obj.model) handlers.onModel?.(obj.model, via);
        if (obj.delta) handlers.onDelta(obj.delta);
        if (obj.error) handlers.onError?.(obj.error);
      } catch { /* skip an unparseable frame rather than abort the stream */ }
    }
    return false;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (flush(decoder.decode(value, { stream: true }))) break;
    }
  } catch {
    if (!signal?.aborted) handlers.onError?.("stream interrupted");
  } finally {
    try { await reader.cancel(); } catch { /* ignore */ }
  }
}

// ---------------------------------------------------------------------------
// Dev fallback: client-side context + direct Ollama (pre-cut-over demo path)
// ---------------------------------------------------------------------------

/** Mirrors the server's fabrication-forbidding system prompt (routes/ask.py). */
const FALLBACK_SYSTEM_PROMPT =
  "You are a terse engineering analyst answering questions about ONE " +
  "computational-aerodynamics project in Thread. You are given a compact " +
  "JSON stats bundle: run counts + statuses, coefficient ranges, and recent " +
  "Tailor fill campaigns (progress, coverage, acquisition reasons, and the " +
  "highest-disagreement points — the honest 'least certain' signal).\n\n" +
  "Hard rules:\n" +
  "- Answer ONLY from the bundle. If the answer is not in it, say exactly " +
  "what is missing — never guess or invent numbers.\n" +
  "- Keep answers short: 1-4 plain sentences, no markdown headers.\n" +
  "- 'Stalled'/'stuck' runs = not_completed_runs + status_counts. 'Least " +
  "certain' = highest_disagreement_points + coverage.\n" +
  "- Quote run names verbatim when naming runs.";

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Compact client-side stats bundle from ALREADY-LIVE endpoints — the same
 *  shape the staged server context uses, so answers read identically. */
export async function buildClientAskContext(
  projectId: string,
  branch: string,
): Promise<Record<string, unknown>> {
  const [table, campaigns] = await Promise.all([
    fetchRunTablePaginated({ projectId, branch }).catch(() => ({ rows: [] as Record<string, unknown>[] })),
    listHighliftCampaigns(projectId, branch).catch(() => []),
  ]);
  const rows = table.rows ?? [];

  const statusCounts: Record<string, number> = {};
  const stuck: string[] = [];
  const failed: string[] = [];
  const cls: Array<{ v: number; name: string }> = [];
  for (const row of rows) {
    const st = String(row.status ?? "unknown").toLowerCase();
    statusCounts[st] = (statusCounts[st] ?? 0) + 1;
    const name = String(row.name ?? "(unnamed)");
    if ((st === "failed" || st === "error") && failed.length < 8) failed.push(name);
    else if ((st === "pending" || st === "running" || st === "queued") && stuck.length < 8) stuck.push(name);
    const cl = num(row.CL);
    if (cl != null) cls.push({ v: cl, name });
  }
  const clMax = cls.length ? cls.reduce((a, b) => (b.v > a.v ? b : a)) : null;
  const clMin = cls.length ? cls.reduce((a, b) => (b.v < a.v ? b : a)) : null;

  // The newest campaign's detail carries reasons + disagreement signals.
  let campaignDetail: Record<string, unknown> | null = null;
  const newest = campaigns[0];
  if (newest) {
    try {
      const detail = await fetchHighliftCampaign(projectId, newest.campaign_id, branch);
      const iterations = detail.state?.iterations ?? [];
      const reasons: string[] = [];
      for (const it of iterations.slice(-6)) {
        for (const r of it.reasons_top ?? []) if (r && !reasons.includes(r)) reasons.push(r);
      }
      const scored = iterations
        .flatMap((it) => it.points ?? [])
        .filter((p) => num(p.disagreement_z) != null)
        .sort((a, b) => Math.abs(b.disagreement_z!) - Math.abs(a.disagreement_z!))
        .slice(0, 5)
        .map((p) => ({ condition: p.condition, disagreement_z: p.disagreement_z, classification: p.classification ?? null }));
      campaignDetail = {
        name: detail.config?.name,
        status: detail.state?.status,
        dispatched: detail.state?.dispatched,
        completed: detail.state?.completed,
        failed: detail.state?.failed,
        coverage: detail.state?.coverage,
        budget: detail.config?.budget,
        recent_acquisition_reasons: reasons.slice(0, 8),
        highest_disagreement_points: scored,
      };
    } catch { /* the summary row still speaks for the campaign */ }
  }

  return {
    branch,
    runs: {
      total_runs: rows.length,
      status_counts: statusCounts,
      not_completed_runs: stuck,
      failed_runs: failed,
      CL: clMax && clMin ? { n: cls.length, min: clMin.v, max: clMax.v, max_at: clMax.name } : null,
    },
    recent_campaigns: campaignDetail ? [campaignDetail] : campaigns.slice(0, 3),
  };
}

async function streamAskDirectOllama(
  projectId: string,
  branch: string,
  question: string,
  handlers: AskStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let context: Record<string, unknown>;
  try {
    context = await buildClientAskContext(projectId, branch);
  } catch {
    context = { branch, error: "project stats unavailable" };
  }
  let bundle = JSON.stringify(context);
  if (bundle.length > 8000) bundle = bundle.slice(0, 8000) + "…(truncated)";

  let res: Response;
  try {
    res = await fetch(`${OLLAMA_URL}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: OLLAMA_FALLBACK_MODEL,
        messages: [
          { role: "system", content: FALLBACK_SYSTEM_PROMPT },
          { role: "user", content: `Project stats bundle:\n${bundle}\n\nQuestion: ${question.trim()}` },
        ],
        stream: true,
        options: { temperature: 0.2, num_predict: 512 },
      }),
      signal,
    });
  } catch {
    if (signal?.aborted) return;
    handlers.onOffline?.("Ask backend not deployed yet and no local Ollama reachable");
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onOffline?.(`local Ollama refused (${res.status})`);
    return;
  }

  handlers.onModel?.(OLLAMA_FALLBACK_MODEL, "ollama-direct");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Ollama streams newline-delimited JSON objects.
      let nl: number;
      // eslint-disable-next-line no-cond-assign
      while ((nl = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) continue;
        try {
          const obj = JSON.parse(line) as { message?: { content?: string }; done?: boolean; error?: string };
          if (obj.error) {
            handlers.onError?.(obj.error);
            return;
          }
          const delta = obj.message?.content;
          if (delta) handlers.onDelta(delta);
          if (obj.done) {
            handlers.onDone?.();
            return;
          }
        } catch { /* skip a partial/unparseable line */ }
      }
    }
    handlers.onDone?.();
  } catch {
    if (!signal?.aborted) handlers.onError?.("stream interrupted");
  } finally {
    try { await reader.cancel(); } catch { /* ignore */ }
  }
}
